import { FormikProps } from 'formik';
import { FiPlus } from 'react-icons/fi';
import FormInput from 'shared/ui/FormInput';
import SelectInput from 'components/shared/SelectInput';

const EVENT_TYPES = [
  {
    label: 'Time Based Script',
    value: 'echo',
    description: 'The avatar will say this at a specified time'
  },
  {
    label: 'Time Based LLM Prompt',
    value: 'system_prompt',
    description: 'Make the LLM aware of something at a certain time.'
  },
  {
    label: 'End Call',
    value: 'end_call',
    description: 'Say something at the end of a call at a specified time.'
  }
];

export const ToolEvents = ({ formik }: { formik: FormikProps<any> }) => {
  let config = { duration: 0, events: [] as any[] };

  try {
    const raw = formik.values.timer_config;
    let parsed: any = raw;

    if (typeof raw === 'string') {
      parsed = JSON.parse(raw);
    }

    config = {
      duration: Number(parsed?.duration) || 0,
      events: Array.isArray(parsed?.events) ? parsed.events : []
    };
  } catch {
    config = { duration: 0, events: [] };
  }

  const updateConfig = (newConfig: typeof config) => {
    formik.setFieldValue('timer_config', newConfig);
  };

  const updateDuration = (newDuration: number) => {
    updateConfig({ ...config, duration: newDuration });
  };

  const addEvent = () => {
    const hasEndCall = config.events.some((e) => e.type === 'end_call');
    const newEvent = {
      type: hasEndCall ? 'time_script' : 'end_call',
      time_point: 0,
      value: ''
    };

    updateConfig({
      ...config,
      events: [...config.events, newEvent]
    });
  };

  const removeEvent = (eventIndex: number) => {
    const updated = config.events.filter((_, i) => i !== eventIndex);
    updateConfig({ ...config, events: updated });
  };

  const handleEventTypeChange = (eventIndex: number, newType: string) => {
    if (newType === 'end_call') {
      const alreadyExists = config.events.some(
        (e, i) => e.type === 'end_call' && i !== eventIndex
      );
      if (alreadyExists) {
        alert('Only one "End Call" event is allowed.');
        return;
      }
    }

    const updated = [...config.events];
    updated[eventIndex].type = newType;
    updateConfig({ ...config, events: updated });
  };

  const handleEventFieldChange = (
    eventIndex: number,
    field: 'time_point' | 'value',
    value: any
  ) => {
    const updated = [...config.events];

    if (field === 'time_point') {
      const clampedValue = Math.min(Number(value), config.duration);
      updated[eventIndex][field] = clampedValue;
    } else {
      updated[eventIndex][field] = value;
    }

    updateConfig({ ...config, events: updated });
  };

  const hasEndCall = config.events.some((e) => e.type === 'end_call');

  return (
    <div className="ag-flex ag-flex-col ag-gap-6">
      <div className="ag-mb-4">
        <label className="ag-text-sm ag-font-medium">Duration (seconds)</label>
        <FormInput
          formik={formik}
          name=""
          type="number"
          value={config.duration}
          onChange={(e) =>
            updateDuration(e.target.value === '' ? 0 : Number(e.target.value))
          }
          className="ag-w-full ag-border ag-border-neutral-300 ag-rounded-md ag-bg-white ag-py-2 ag-px-3 ag-text-sm ag-text-neutral-900 placeholder:ag-text-neutral-500 focus:ag-border-purple-500 focus:ag-ring-0 focus:ag-outline-none"
          placeholder="Enter duration"
        />
      </div>

      <div className="ag-flex ag-justify-between ag-items-center ag-mb-2">
        <span className="ag-font-semibold ag-text-base">Events</span>
        <button
          type="button"
          disabled={hasEndCall}
          onClick={addEvent}
          className={`ag-text-white ag-font-medium ag-text-sm ag-px-4 ag-py-2 ag-rounded-md ag-flex ag-items-center ag-gap-1 ${
            hasEndCall
              ? 'ag-bg-neutral-400 cursor-not-allowed'
              : 'ag-bg-purple-500 hover:ag-bg-purple-600'
          }`}
        >
          <FiPlus size={16} /> Add New Event
        </button>
      </div>

      {config.events.map((event: any, eventIndex: number) => (
        <div
          key={eventIndex}
          className="ag-border ag-border-neutral-200 ag-flex ag-flex-col ag-gap-5 ag-rounded-lg ag-p-4 ag-bg-white"
        >
          <div>
            <SelectInput
              name=""
              options={EVENT_TYPES.map((type) => type.label)}
              value={
                EVENT_TYPES.find((t) => t.value === event.type)?.label || ''
              }
              optionsWrapperClassName="ag-bg-white ag-border ag-border-neutral-300"
              className="ag-bg-white !ag-border-none !ag-shadow-none !ag-text-neutral-800 !ag-w-auto ag-text-sm !ag-px-0 !ag-justify-start"
              wrapperClassName="!ag-w-[50%]"
              optionsClassName="ag-text-sm ag-text-neutral-700 hover:ag-bg-neutral-100"
              activeClassName="ag-bg-neutral-200"
              cheronNearText
              onChange={(selectedLabel) => {
                const selectedType = EVENT_TYPES.find(
                  (t) => t.label === selectedLabel
                );
                if (selectedType) {
                  handleEventTypeChange(eventIndex, selectedType.value);
                }
              }}
            />
            <p className="ag-text-sm ag-text-neutral-400">
              {EVENT_TYPES.find((t) => t.value === event.type)?.description}
            </p>
          </div>

          <div className="ag-grid ag-grid-cols-2 ag-gap-4 ag-items-center">
            <div className="ag-flex ag-items-center">
              <FormInput
                formik={formik}
                name=""
                type="number"
                value={event.time_point}
                onChange={(e) =>
                  handleEventFieldChange(
                    eventIndex,
                    'time_point',
                    e.target.value === '' ? '' : Number(e.target.value)
                  )
                }
                className="ag-w-full ag-border ag-border-neutral-300 ag-rounded-md ag-bg-white ag-py-2 ag-px-3 ag-text-sm ag-text-neutral-900 placeholder:ag-text-neutral-500 focus:ag-border-purple-500 focus:ag-ring-0 focus:ag-outline-none"
                placeholder="0"
              />
              <span className="ag-text-sm ag-text-neutral-500 ag-ml-2">
                Sec
              </span>
            </div>

            <FormInput
              formik={formik}
              name=""
              type="text"
              value={event.value || ''}
              onChange={(e) =>
                handleEventFieldChange(eventIndex, 'value', e.target.value)
              }
              className="ag-w-full ag-border ag-border-neutral-300 ag-rounded-md ag-bg-white ag-py-2 ag-px-3 ag-text-sm ag-text-neutral-900 placeholder:ag-text-neutral-500 focus:ag-border-purple-500 focus:ag-ring-0 focus:ag-outline-none"
              placeholder="Enter Message"
            />
          </div>

          <button
            type="button"
            onClick={() => removeEvent(eventIndex)}
            className="ag-mt-2 ag-text-sm ag-text-red-500 hover:ag-text-red-700"
          >
            Remove Event
          </button>
        </div>
      ))}
    </div>
  );
};
